import React, { useEffect, useState } from 'react'
import { MdCheckCircle } from 'react-icons/md'

/**
 * Step 2 — confirmation after the customer has asked for a magic link.
 *
 * Backend always returns success regardless of whether the identifier
 * matched a real account (this is deliberate, prevents enumeration via
 * the magic-link endpoint), so this screen is intentionally vague about
 * "if we found a match". The customer learns yes-or-no by whether they
 * receive an email.
 *
 * Resend button is gated by a 30s cooldown so a frustrated customer
 * can't hammer the endpoint. The cooldown is purely cosmetic on top of
 * server-side rate-limit middleware (3/min on /magic-link/request).
 */

interface Props {
  channel: 'email' | 'sms'
  onResend: () => void
  onBack: () => void
}

const RESEND_COOLDOWN_SECONDS = 30

function MagicLinkSentStep({ channel, onResend, onBack }: Props): React.JSX.Element {
  const [secondsRemaining, setSecondsRemaining] = useState(RESEND_COOLDOWN_SECONDS)

  useEffect(() => {
    if (secondsRemaining <= 0) return
    const id = setTimeout(() => setSecondsRemaining(secondsRemaining - 1), 1000)
    return () => clearTimeout(id)
  }, [secondsRemaining])

  const handleResend = (): void => {
    onResend()
    setSecondsRemaining(RESEND_COOLDOWN_SECONDS)
  }

  return (
    <div className="login-step login-step--neutral">
      <div className="login-step-icon login-step-icon--success">
        <MdCheckCircle size={48} />
      </div>
      <h1 className="login-step-heading">
        {channel === 'sms' ? 'Check your phone' : 'Check your email'}
      </h1>
      <p className="login-step-subheading">
        If we found a matching account, a one-time sign-in link is on its way
        {channel === 'sms' ? ' by text' : ' to the email on file'}.
        It expires in 15 minutes.
      </p>

      <div className="login-info-card muted">
        <p>
          <strong>Didn't get it?</strong>{' '}
          {channel === 'sms'
            ? 'Texts can take a minute to arrive.'
            : 'Check your spam folder.'}
        </p>
      </div>

      <button
        type="button"
        className="btn btn-secondary btn-lg btn-block"
        onClick={handleResend}
        disabled={secondsRemaining > 0}
      >
        {secondsRemaining > 0
          ? `Resend in ${secondsRemaining}s`
          : 'Resend the sign-in link'}
      </button>

      <div className="login-step-footer">
        <button type="button" className="btn btn-link" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  )
}

export default MagicLinkSentStep
